import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { RbacApi, type AppUser } from "../../admin/rbac/api";
import { useScopeNames } from "../../admin/hierarchy/useScopeNames";
import { useUsersById } from "../../admin/rbac/useUsersById";
import { useMarkSeen } from "../../notifications/useMarkSeen";
import { SearchableSelect } from "../../shell/SearchableSelect";
import { Wizard, WizardField, WizardSection, ScopePicker, type WizardStep } from "../../wizard/Wizard";
import { TasksApi, type Task, type TaskPriority, type TaskStatus } from "./api";

export function TasksRoutes() {
  return (
    <Routes>
      <Route index element={<TasksList />} />
      <Route path="new" element={<NewTask />} />
      <Route path=":id" element={<TaskDetail />} />
    </Routes>
  );
}

function TasksList() {
  const [view, setView] = useState<"all" | "mine">("mine");
  const [status, setStatus] = useState<TaskStatus | "">("");
  const [priority, setPriority] = useState<TaskPriority | "">("");

  const all = useQuery({
    queryKey: ["tasks", status],
    queryFn: () => TasksApi.list(status ? { status } : undefined),
    enabled: view === "all",
  });
  const mine = useQuery({
    queryKey: ["my-tasks", status],
    queryFn: () => TasksApi.myTasks(status || undefined),
    enabled: view === "mine",
    refetchInterval: 30_000,
  });

  const data = view === "mine" ? mine : all;
  useMarkSeen("tasks", data.dataUpdatedAt);
  const { name: userName } = useUsersById();
  const items = (data.data?.items ?? [])
    .filter((t) => !priority || t.priority === priority)
    .slice()
    .sort((a, b) => (b.assignedAt ?? "").localeCompare(a.assignedAt ?? ""));

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Your Tasks</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            {view === "mine"
              ? "Tasks you need to do, sorted by what's most urgent. Each is identified by a code — your senior briefs you on the details out-of-band."
              : "Tasks you've assigned to others — track who's doing what."}
          </p>
        </div>
        <Link to="new"><button>Assign task</button></Link>
      </header>

      <div className="row-3" style={{ marginTop: 16 }}>
        <label>
          View:
          <select value={view} onChange={(e) => setView(e.target.value as typeof view)}>
            <option value="mine">Tasks for me</option>
            <option value="all">Tasks I assigned</option>
          </select>
        </label>
        <label>
          Status:
          <select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus | "")}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label>
          Priority:
          <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority | "")}>
            <option value="">All</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
      </div>

      {data.isLoading && <p>Loading…</p>}
      {data.error && <p className="error">{(data.error as Error).message}</p>}

      <div className="card-grid">
        {items.map((t) => (
          <Link key={t.id} to={t.id} className="list-card">
            <div className="list-card-header">
              <PriorityBadge priority={t.priority} />
              <StatusBadge status={t.status} />
            </div>
            <div className="list-card-title">{t.title ?? t.code ?? "(coded task)"}</div>
            <div className="list-card-snippet">
              {t.title
                ? (t.description || "No description.")
                : <>Coded task {t.code}. Speak to your senior for details.</>}
            </div>
            <div className="list-card-foot">
              <span>{t.title ? `For ${userName(t.assigneeUid)}` : t.code}</span>
              <span>{t.dueAt ? `Due ${new Date(t.dueAt).toLocaleDateString()}` : (t.assignedAt ? new Date(t.assignedAt).toLocaleDateString() : "—")}</span>
            </div>
          </Link>
        ))}
        {items.length === 0 && !data.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            {view === "mine"
              ? "No tasks waiting. Done tasks are hidden — they disappear once you mark them complete."
              : "No tasks."}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const t = useQuery({ queryKey: ["task", id], queryFn: () => TasksApi.get(id), enabled: !!id });
  const { name: userName } = useUsersById();
  const { branchName } = useScopeNames();

  const setStatus = useMutation({
    mutationFn: (next: TaskStatus) => TasksApi.update(id, { status: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: () => TasksApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
      nav("..", { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  if (t.isLoading) return <p>Loading…</p>;
  if (t.error) return <p className="error">{(t.error as Error).message}</p>;
  if (!t.data) return null;

  // Recipient-only view: the API strips title / description / assignedBy
  // / related* before sending. Detect that and render a minimal "code only"
  // detail page with just the action buttons.
  const isRecipientView = !t.data.title && !!t.data.code;

  return (
    <div className="hierarchy">
      <header>
        <Link to=".." className="muted">← Back</Link>
        <h1 style={{ marginTop: 4 }}>
          {t.data.title ?? t.data.code ?? "(task)"}{" "}
          <PriorityBadge priority={t.data.priority} />
          <StatusBadge status={t.data.status} />
        </h1>
      </header>
      {error && <div className="error">{error}</div>}

      {isRecipientView ? (
        <>
          <p className="muted" style={{ fontSize: 13 }}>
            This is a coded task. Your senior briefs you on what to do — the details aren't carried in the system.
            When you've completed it, click <strong>Mark done</strong> below and the task disappears from your list.
          </p>
          <dl className="kv">
            <dt>Code</dt><dd><strong>{t.data.code}</strong></dd>
            <dt>Priority</dt><dd>{t.data.priority}</dd>
            <dt>Due</dt><dd>{t.data.dueAt ? new Date(t.data.dueAt).toLocaleString() : "—"}</dd>
          </dl>
        </>
      ) : (
        <dl className="kv">
          {t.data.code && (<><dt>Code</dt><dd><strong>{t.data.code}</strong></dd></>)}
          <dt>Title</dt><dd>{t.data.title || "—"}</dd>
          <dt>Description</dt><dd style={{ whiteSpace: "pre-wrap" }}>{t.data.description || "—"}</dd>
          <dt>Assigned to</dt><dd>{userName(t.data.assigneeUid)}</dd>
          <dt>Assigned by</dt><dd>{userName(t.data.assignedBy)}</dd>
          <dt>Branch</dt><dd>{branchName(t.data.branchId)}</dd>
          <dt>Due</dt><dd>{t.data.dueAt ? new Date(t.data.dueAt).toLocaleString() : "—"}</dd>
          <dt>Related</dt><dd>{t.data.relatedType ? `${t.data.relatedType}/${t.data.relatedId}` : "—"}</dd>
        </dl>
      )}

      <div className="row-actions" style={{ marginTop: 16 }}>
        {t.data.status === "open" && (
          <button
            onClick={() => {
              if (window.confirm("Start this task? It moves to 'in progress' so people know you're on it.")) setStatus.mutate("in_progress");
            }}
          >Start</button>
        )}
        {t.data.status !== "done" && t.data.status !== "cancelled" && (
          <button
            onClick={() => {
              if (window.confirm("Mark this task done? You can reopen it if needed.")) setStatus.mutate("done");
            }}
          >Mark done</button>
        )}
        {t.data.status === "done" && (
          <button
            className="ghost"
            onClick={() => {
              if (window.confirm("Reopen this task? It returns to 'open' status.")) setStatus.mutate("open");
            }}
          >Reopen</button>
        )}
        {t.data.status !== "cancelled" && (
          <button
            className="ghost"
            onClick={() => {
              if (window.confirm("Cancel this task? It stays in the record but is marked cancelled.")) setStatus.mutate("cancelled");
            }}
          >Cancel</button>
        )}
        {isSuperadmin && (
          <button
            className="danger"
            style={{ display: "none" }}
            onClick={() => {/* delete disabled — use Cancel to mark cancelled */}}
          >Delete</button>
        )}
      </div>
    </div>
  );
}

function NewTask() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { uid: myUid } = useMe();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // When the task wizard is opened from a record's "Assign as task" button,
  // we receive these params and pre-fill the form so the user just picks
  // the assignee and submits.
  const presetRelatedType = searchParams.get("relatedType") as ("case" | "intel" | "poi" | "performance" | "popularity" | null);
  const presetRelatedId = searchParams.get("relatedId");
  const presetTitle = searchParams.get("title") ?? "";

  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const scope = useQuery({ queryKey: ["my-scope"], queryFn: HierarchyApi.myScope });
  const assignableUsers = (users.data?.items ?? []).filter((u) => u.id !== myUid);
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const constituencies = scope.data?.constituencies ?? [];
  const wardsForCid = (scope.data?.wards ?? []).filter((w) => w.constituencyId === cid);
  const branchesForWid = (scope.data?.branches ?? []).filter((b) => b.wardId === wid);

  const [title, setTitle] = useState(presetTitle);
  const [description, setDescription] = useState(
    presetRelatedType && presetRelatedId
      ? `Linked from ${presetRelatedType} ${presetRelatedId}.\n\n`
      : "",
  );
  const [assigneeUid, setAssigneeUid] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [dueAt, setDueAt] = useState("");
  // Free-text skill tag for this task. The wizard sorts assignable users
  // so the ones whose role/personal specialisations include this tag
  // surface first.
  const [taskSpecialisation, setTaskSpecialisation] = useState("");

  // Roles list lets us look up role.specialisations when scoring users.
  const rolesQuery = useQuery({ queryKey: ["roles"], queryFn: RbacApi.listRoles });
  const assignmentsQuery = useQuery({
    queryKey: ["assignments-all"],
    queryFn: () => RbacApi.listAssignments(),
  });

  // Build a per-user tag bag = personal specialisations ∪ specialisations of
  // every role they hold (via assignments). Sort assignable users so any
  // overlap with `taskSpecialisation` floats to the top.
  function tagsFor(u: AppUser): string[] {
    const personal = u.specialisations ?? [];
    const myRoleIds = (assignmentsQuery.data?.items ?? [])
      .filter((a) => a.userId === u.id)
      .map((a) => a.roleId);
    const fromRoles = (rolesQuery.data?.items ?? [])
      .filter((r) => myRoleIds.includes(r.id))
      .flatMap((r) => r.specialisations ?? []);
    return [...new Set([...personal, ...fromRoles])];
  }
  const wantedTag = taskSpecialisation.trim().toLowerCase();
  const sortedAssignable = wantedTag
    ? [...assignableUsers].sort((a, b) => {
        const aMatch = tagsFor(a).includes(wantedTag) ? 0 : 1;
        const bMatch = tagsFor(b).includes(wantedTag) ? 0 : 1;
        return aMatch - bMatch;
      })
    : assignableUsers;

  const create = useMutation({
    mutationFn: TasksApi.create,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      nav(`../${t.id}`, { replace: true });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });

  function submit() {
    if (!title || !bid || !assigneeUid) return;
    create.mutate({
      title,
      description,
      assigneeUid,
      branchId: bid,
      priority,
      dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
      // Preserve the link back to the source record so completing this task
      // can auto-promote the source from pending → in_progress.
      relatedType: presetRelatedType ?? undefined,
      relatedId: presetRelatedId ?? undefined,
    });
  }

  const steps: WizardStep[] = [
    {
      title: "Where",
      canNext: !!bid,
      blockerHint: "Pick the branch this task belongs to",
      body: (
        <WizardSection title="Where does this task belong?">
          <ScopePicker
            constituencies={constituencies}
            wards={wardsForCid}
            branches={branchesForWid}
            cid={cid} wid={wid} bid={bid}
            onCidChange={(v) => { setCid(v); setWid(""); setBid(""); }}
            onWidChange={(v) => { setWid(v); setBid(""); }}
            onBidChange={setBid}
          />
        </WizardSection>
      ),
    },
    {
      title: "Assignee",
      canNext: !!assigneeUid,
      blockerHint: "Pick someone to assign the task to",
      body: (
        <WizardSection title="Who is doing this?" hint="The recipient sees only the task code (e.g. TSK-A1B2). Brief them on the details out-of-band.">
          <WizardField label="Skill needed (optional)" hint="If this task needs a specific skill — e.g. surveillance, cyber — type it here. The Assignee dropdown will float matching users to the top.">
            <input
              value={taskSpecialisation}
              onChange={(e) => setTaskSpecialisation(e.target.value)}
              placeholder="e.g. surveillance, logistics"
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
            />
          </WizardField>
          <WizardField label="Assignee" hint={
            wantedTag
              ? `People with "${wantedTag}" appear first. Personal specialisations + role specialisations both count.`
              : "Type to search. Only people who report into your chain of command appear here."
          }>
            <SearchableSelect
              value={assigneeUid}
              onChange={setAssigneeUid}
              options={sortedAssignable.map((u: AppUser) => {
                const tags = tagsFor(u);
                const matches = wantedTag && tags.includes(wantedTag);
                return {
                  value: u.id,
                  label: matches ? `★ ${u.fullName || u.email}` : (u.fullName || u.email),
                  hint: tags.length > 0 ? tags.join(", ") : u.email,
                };
              })}
              required
            />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Details",
      canNext: !!title.trim(),
      blockerHint: "Title is required",
      body: (
        <WizardSection title="Task details" hint="The title and description are visible to YOU and superadmins only — not to the recipient.">
          <WizardField label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Verify branch register" />
          </WizardField>
          <WizardField label="Description">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Specifics of what needs to happen, why, and what to bring back." />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Priority",
      canNext: true,
      body: (
        <WizardSection title="Priority + due date" hint="Urgent + High priority push notifications immediately.">
          <WizardField label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </WizardField>
          <WizardField label="Due at" hint="Optional. Overdue tasks push a reminder to the recipient.">
            <input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </WizardField>
        </WizardSection>
      ),
    },
    {
      title: "Review",
      canNext: !!title.trim() && !!bid && !!assigneeUid,
      body: (
        <WizardSection title="Review and assign">
          <dl className="kv">
            <dt>Title</dt><dd>{title}</dd>
            <dt>Assignee</dt><dd>{assignableUsers.find((u) => u.id === assigneeUid)?.fullName ?? assigneeUid}</dd>
            <dt>Branch</dt><dd>{branchesForWid.find((b) => b.id === bid)?.name ?? bid}</dd>
            <dt>Priority</dt><dd>{priority}</dd>
            <dt>Due</dt><dd>{dueAt ? new Date(dueAt).toLocaleString() : "—"}</dd>
            <dt>Description</dt><dd style={{ whiteSpace: "pre-wrap" }}>{description || "—"}</dd>
          </dl>
        </WizardSection>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <Wizard
        title="Assign task"
        steps={steps}
        busy={create.isPending}
        submitLabel="Assign"
        onSubmit={submit}
        onCancel={() => nav("..")}
      />
    </div>
  );
}

function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const colors: Record<TaskPriority, string> = {
    low: "#3a8053",
    medium: "#a87b1c",
    high: "#c0531a",
    urgent: "#b14444",
  };
  return (
    <span style={{
      marginLeft: 8, fontSize: 11, padding: "2px 6px",
      background: colors[priority], color: "white", borderRadius: 4,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>{priority}</span>
  );
}

function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span style={{
      marginLeft: 6, fontSize: 11, padding: "2px 6px",
      background: "var(--border)", color: "var(--text)", borderRadius: 4,
    }}>{status.replace("_", " ")}</span>
  );
}
